import React, { Dispatch, SetStateAction, useState } from "react"
import Popup from "../../../Ui/Popup"
import {
  CustomReasonCode,
  InputActionBonus,
  InputActionGiftCard,
  InputActionPaymentCancellation,
  InputActionType,
  InputOrderCancellation,
  InputReason,
  Maybe,
  Order
} from "lib/types/generated/graphql-types"
import { useMutation, useQuery } from "@apollo/client"
import CREATE_CANCELLATION from "../../../../graphql/mutations/order/cancellation/CreateCancellation"
import DELETE_CANCELLATION from "../../../../graphql/mutations/order/cancellation/DeleteCancellation"
import CancellationAction from "./CancellationAction"
import START_CANCELLATION from "../../../../graphql/mutations/order/cancellation/StartCancellation"
import StatusPopup from "./StatusPopup"
import { handleErrorMessages } from "helpers/errors"
import ReasonInput from "../Reason/ReasonInput"
import ALL_REASON_CODES from "../../../../graphql/queries/settings/reasonCodes/AllReasonCodes"
import { useUserGroups } from "../../../../hooks/useUserGroups"
import { PermissionGroups } from "../../../../routes/constants"
import AddedReason from "../Reason/AddedReason"
import { WithAuth } from "../../../Auth"
import { ReasonType } from "lib/types/order"

type Props = {
  setShowCancelPopup: Dispatch<SetStateAction<boolean>>
  showCancelPopup: boolean
  order: Order
  refetch: () => void
  refetchLoading: boolean
}

const CancelOrderPopup = ({
  setShowCancelPopup,
  order,
  refetch,
  showCancelPopup,
  refetchLoading
}: Props) => {
  const [reason, setReason] = React.useState<InputReason>({ cause: "", code: "" })
  const [reasonType, setReasonType] = React.useState<ReasonType | undefined>()
  const [showStatusPopup, setShowStatusPopup] = useState(false)
  const [giftCardAction, setGiftCardAction] = useState<InputActionGiftCard>({
    actionType: InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<InputActionBonus>({
    actionType: InputActionType.Auto
  })
  const [paymentAction, setPaymentAction] = useState<InputActionPaymentCancellation>({
    actionType: InputActionType.Auto
  })

  const { hasAnyGroup } = useUserGroups()

  const [createCancellation, { loading: createLoading }] = useMutation(CREATE_CANCELLATION, {
    onCompleted: () => {
      setReason({ cause: "", code: "" })
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const [deleteCancellation, { loading: deleteLoading }] = useMutation(DELETE_CANCELLATION, {
    onCompleted: () => {
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const [startCancellation, { loading: startLoading }] = useMutation(START_CANCELLATION, {
    onCompleted: () => {
      setShowCancelPopup(false)
      setShowStatusPopup(true)
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const createOrderCancellation = () => {
    const input: InputOrderCancellation = {}
    const inputReason: InputReason = {}
    if (reason.code != "") {
      inputReason.code = reason.code
    }
    if (reason.cause != "") {
      inputReason.cause = reason.cause
    }
    if (Object.keys(inputReason).length > 0 || reasonType === ReasonType.SKIP) {
      input.reason = inputReason
    }

    return createCancellation({
      variables: {
        orderId: order.id,
        input: input
      }
    })
  }

  const deleteOrderCancellation = () => {
    if (order.Cancellations[0]?.id)
      deleteCancellation({ variables: { cancellationId: order.Cancellations[0].id } })
    setShowCancelPopup(false)
  }

  const startOrderCancellation = () => {
    if (hasAnyGroup(PermissionGroups.orders.execute)) {
      startCancellation({
        variables: {
          cancellationId: order.Cancellations[0].id,
          input: {
            ...(order.Cancellations[0]?.bonus && { bonus: bonusAction }),
            ...(order.Cancellations[0].giftCards.length > 0 && { giftCard: giftCardAction }),
            payment: paymentAction
          }
        }
      })
    }
  }

  const { data } = useQuery(ALL_REASON_CODES)
  const customReasons = data?.Order?.customReasonCodes?.codes

  const getCustomCode = (code: Maybe<string> | undefined) =>
    customReasons?.find((customCode: CustomReasonCode) => customCode.code === code)

  if (showStatusPopup) {
    return (
      <StatusPopup
        setShowStatusPopup={setShowStatusPopup}
        cancellationId={order?.Cancellations[0].id}
        refetchOrder={refetch}
      />
    )
  }

  if (showCancelPopup) {
    return (
      <Popup
        title={`Cancel order`}
        handleCloseClick={() => {
          setShowCancelPopup(false)
        }}
        buttonText={
          order?.Cancellations?.length > 0
            ? hasAnyGroup(PermissionGroups.orders.execute)
              ? "Confirm cancellation"
              : "Needs admin access"
            : "Next"
        }
        handleOkClick={() =>
          order?.Cancellations?.length > 0 ? startOrderCancellation() : createOrderCancellation()
        }
        disableOkButton={((!reason?.cause || !reason.code) && reasonType !== ReasonType.SKIP) && order?.Cancellations?.length === 0}
        loading={createLoading || startLoading || refetchLoading}
        cancelOperation={deleteOrderCancellation}
        cancelOperationLoading={deleteLoading}
      >
        <b>Reason</b>
        {order?.Cancellations?.length > 0 ? (
          <AddedReason reason={order?.Cancellations[0]?.reason} getCustomCode={getCustomCode} disableDelete={true}/>
        ) : (
          <ReasonInput
            reason={reason}
            setReason={setReason}
            customReasons={customReasons}
            reasonType={reasonType}
            setReasonType={setReasonType}
          />
        )}
        {order?.Cancellations?.length > 0 && (
          <WithAuth requiredGroups={PermissionGroups.orders.execute}>
            <CancellationAction
              cancellation={order?.Cancellations[0]}
              actionState={{
                giftCardAction,
                setGiftCardAction,
                bonusAction,
                setBonusAction,
                paymentAction,
                setPaymentAction
              }}
              orderId={order.id}
            />
          </WithAuth>
        )}
      </Popup>
    )
  }
  return <></>
}

export default CancelOrderPopup
